import autobind from 'autobind-decorator';
import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from '@shared/auth';
import { userManager } from '@shared/auth/user-manager';

const UNAUTHORIZED_ERROR_CODE = 401;
const ACCESS_ERROR_CODE = 403;
const AXIOS_INSTANCE_CONFIG_API = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};
const AXIOS_INSTANCE_CONFIG_ROOT = {
  headers: {},
};

function logRequest(requestConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  return requestConfig;
}

function addAuthorizationHeader(requestConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = getAccessToken();
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
}

function getConfig(root: boolean, withCredentials: boolean): AxiosRequestConfig {
  const config = root || !withCredentials ? AXIOS_INSTANCE_CONFIG_ROOT : AXIOS_INSTANCE_CONFIG_API;
  return { ...config, withCredentials };
}

function createAxiosInstance(
  onErrorInterceptor: (error: AxiosError) => Promise<AxiosResponse<unknown>>,
  config: AxiosRequestConfig,
): AxiosInstance {
  const instance = Axios.create(config);
  instance.interceptors.response.use(
    response => {
      // logResponse(response)
      return response;
    },
    error => {
      // logResponseError(error)
      return onErrorInterceptor(error);
    },
  );
  instance.interceptors.request.use(addAuthorizationHeader);
  instance.interceptors.request.use(logRequest);
  return instance;
}

let silentRenewPromise: Promise<unknown> | null = null;

export class RequestExecuter {
  private axiosInstance: AxiosInstance;

  constructor(withCredentials: boolean) {
    const config = getConfig(false, withCredentials);
    this.axiosInstance = createAxiosInstance(this.errorInterceptor, config);
  }

  @autobind
  public makeRequest<T = unknown>(params: AxiosRequestConfig, error: string | null = null): Promise<T> {
    const getResponseError = this.getResponseError;
    return new Promise((resolve, reject) => {
      this.axiosInstance
        .request<T>(params)
        .then(response => resolve(response.data))
        .catch(responseError => reject(getResponseError(responseError, error)));
    });
  }

  @autobind
  public makeRawRequest<T = unknown>(params: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.request<T>(params);
  }

  @autobind
  public makeRequestWithAbort<T = unknown>(
    params: AxiosRequestConfig,
    error: string | null = null,
  ): [() => Promise<T>, AbortController] {
    const abortController = new AbortController();
    const wrappedParams = { ...params, signal: abortController.signal };
    const request = (): Promise<T> => this.makeRequest<T>(wrappedParams, error);

    return [request, abortController];
  }

  @autobind
  getResponseError(responseError: AxiosError, error: string | null): Error | unknown {
    const responseCode = responseError.response && responseError.response.status;
    if (error || responseCode === 500) {
      return new Error(error ? `${error}` : JSON.stringify(responseError));
    } else {
      const responseData = responseError.response && responseError.response.data;
      return responseData ? responseData : responseError;
    }
  }

  @autobind
  private errorInterceptor(error: AxiosError): Promise<AxiosResponse<unknown>> {
    if (error.message === 'Cancelled') {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (status === UNAUTHORIZED_ERROR_CODE) {
      if (!silentRenewPromise) {
        silentRenewPromise = userManager
          .signinSilent()
          .catch(() => userManager.signinRedirect())
          .finally(() => {
            silentRenewPromise = null;
          });
      }

      if (!originalRequest) {
        return Promise.reject(error);
      }

      return silentRenewPromise.then(() => {
        const request = addAuthorizationHeader(originalRequest);
        return this.axiosInstance.request(request);
      });
    }

    if (status === ACCESS_ERROR_CODE) {
      window.location.replace('/subscription-error');
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
}
